import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageLayout from '../layout/PageLayout';

/**
 * Purpose: Shared immersive shell for Islam hub subpages (gradient field, glass content, back nav).
 * Inputs: browserTitle, metaDescription, icon, heading, tagline, accentOrbClass (Tailwind), optional wide (wider max-width for dense grids), children.
 * Outputs: Page wrapped in PageLayout with Helmet and styled main region.
 */
const IslamFuturisticShell = ({
  browserTitle,
  metaDescription,
  icon,
  heading,
  tagline,
  accentOrbClass = 'bg-cyan-500/25',
  wide = false,
  noIndex = false,
  children,
}) => (
  <PageLayout title={browserTitle || 'Islam — PakPoints'}>
    <Helmet>
      <title>{browserTitle || 'Islam — PakPoints'}</title>
      {noIndex ? <meta name="robots" content="noindex,follow" /> : null}
      {metaDescription ? <meta name="description" content={metaDescription} /> : null}
    </Helmet>
    <div className="relative min-h-[calc(100vh-10rem)] overflow-hidden bg-slate-950 text-slate-100">
      <div
        className="pointer-events-none absolute inset-0 opacity-90"
        style={{
          backgroundImage:
            'radial-gradient(ellipse 120% 80% at 50% -20%, rgba(99, 102, 241, 0.35), transparent 55%), radial-gradient(ellipse 80% 50% at 100% 50%, rgba(6, 182, 212, 0.12), transparent 45%), radial-gradient(ellipse 60% 40% at 0% 80%, rgba(168, 85, 247, 0.15), transparent 50%)',
        }}
        aria-hidden
      />
      <div
        className={`pointer-events-none absolute -top-24 right-0 h-80 w-80 rounded-full blur-3xl ${accentOrbClass}`}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-0 left-1/4 h-64 w-64 rounded-full bg-indigo-600/20 blur-3xl"
        aria-hidden
      />

      <div
        className={`relative z-10 mx-auto px-4 py-10 sm:px-6 sm:py-14 lg:px-8 ${wide ? 'max-w-7xl' : 'max-w-4xl'}`}
      >
        <Link
          to="/islam"
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 backdrop-blur-md transition hover:border-cyan-400/40 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
        >
          <span aria-hidden>←</span>
          Islam hub
        </Link>

        <header className="mb-10 text-center sm:text-left">
          <div className="mb-4 text-5xl sm:text-6xl" aria-hidden>
            {icon}
          </div>
          <h1 className="bg-gradient-to-r from-white via-cyan-100 to-violet-200 bg-clip-text text-3xl font-extrabold tracking-tight text-transparent sm:text-4xl">
            {heading}
          </h1>
          {tagline ? (
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{tagline}</p>
          ) : null}
        </header>

        <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-6 shadow-[0_0_60px_-12px_rgba(34,211,238,0.25)] backdrop-blur-xl sm:p-10">
          {children}
        </div>
      </div>
    </div>
  </PageLayout>
);

export default IslamFuturisticShell;
